import { useState, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Accordion from '@base/Accordion/Accordion';
import ChevronRight from '@icon/ChevronRight';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { getOfferList, getOfferFaqs } from '@selectors/checkout';
import { getTopology } from '@selectors/topologies';
import useTTUStore from '@stores/ttu';
import { getTTUIsGoalEnabled } from '@stores/selectors/ttuSelectors';
import Banner from '@cont/TalkToUnacademy/Banner';
import Image from '@base/Image';
import Divider from '@base/Divider/Divider';
import useWindowSize from '@hooks/useWindowSize';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { useSelector } from 'react-redux';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { segment } from '@actions/analytics';
import { pluralize } from '@utils/textHelper';
import { getIsSubscribed } from '@selectors/subscription';
import { CHECKOUT_OFFER_FAQS_VIEWED } from '../constants/segment';
import OffersSection from './OffersSection';

const offerIcon = 'checkout/offer.svg';
const faqIcon = 'checkout/faq.svg';

const Flex = styled.div`
  display: flex;
`;

const FlexCenter = styled(Flex)`
  align-items: center;
`;

const MainWrap = styled.div`
  min-height: calc(100vh - 358px);
  width: 1136px;
  margin: ${Spacings.SPACING_13B} auto 0 auto;
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-top: ${Spacings.SPACING_6B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_3B};
    margin: 0;
  }
`;

const OfferCont = styled(Flex)`
  ${mediaQuery.uptoTablet} {
    flex-direction: column;
  }
`;

const OfferLeftCont = styled.div`
  position: relative;
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_9B};
  }
`;

const StickyCont = styled.div`
  position: sticky;
  top: 124px;
`;

const OfferLeftContFlexWrap = styled(Flex)`
  flex-direction: column;
  ${mediaQuery.uptoTablet} {
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
  }
`;

const OfferIcon = styled.div`
  opacity: ${({ $isDarkModeOn }) => ($isDarkModeOn ? '1' : '0.75')};
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: 0;
    margin-left: auto;
  }
`;

const HeaderCont = styled.div`
  width: 82%;
  margin-bottom: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoTablet} {
    width: 202px;
  }
`;

const StyledH2 = styled(H2)`
  margin-bottom: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H3};
  }
`;

const StyledP = styled(P1)`
  font-size: 14px;
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.P2};
  }
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  width: fit-content;
  &:hover {
    margin-top: -${Spacings.SPACING_2};
  }
`;

const LinkText = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.P2};
  }
`;

const OfferRightCont = styled.div`
  width: 71%;
  margin-left: auto;
  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
`;

const FaqWrap = styled.div`
  text-align: center;
  margin-top: 160px;
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_13B};
  }
`;

const FaqHeader = styled(H2)`
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const FaqCont = styled(Flex)`
  flex-wrap: wrap;
  margin-top: ${Spacings.SPACING_14B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_9B};
  }
`;

const AccordionWrap = styled.div`
  text-align: left;
  margin-left: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  &:nth-child(odd) {
    margin-left: 0;
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin-left: 0;
    margin-bottom: ${Spacings.SPACING_5B};
  }
`;

const StyledAccordion = styled(Accordion)`
  width: 560px;
  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
  &.MuiAccordion-root {
    padding: ${Spacings.SPACING_6B};
    background-color: var(--color-base-1);
    box-shadow: ${boxShadowGenerator()};
    margin: 0;
    min-height: 48px;
    ${mediaQuery.uptoTablet} {
      padding: ${Spacings.SPACING_4B};
      min-height: inherit;
    }

    &.Mui-expanded {
      min-height: inherit;
      background-color: var(--color-base-1);
      box-shadow: ${boxShadowGenerator()};
    }
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  min-height: inherit;
  &.MuiAccordionSummary-root {
    &.Mui-expanded {
      margin-bottom: ${Spacings.SPACING_4B};
      ${mediaQuery.uptoTablet} {
        margin-bottom: ${Spacings.SPACING_2B};
      }
    }
  }
`;

const StyledChevron = styled(ChevronRight)`
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(-90deg)' : 'rotate(-270deg)'};
  transition: transform 200ms ease;
  margin-left: auto;
  flex-shrink: 0;
`;

const FaqQuestion = styled(H5)`
  margin-right: ${Spacings.SPACING_4B};
`;

const StyledBanner = styled(Banner)`
  margin: ${Spacings.SPACING_20B} auto;
`;

const groupBy = (items, key) =>
  items.reduce(
    (result, item) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item]
    }),
    {}
  );

const OfferContHead = styled(P1)`
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  margin-bottom: ${Spacings.SPACING_5B};
  margin-left: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_3B};
    margin-left: 0;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0 ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    margin: 0;
  }
`;

const Offers = () => {
  const { query } = useRouter();
  const { goalUID } = query;
  const faqRef = useRef();
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const { isTablet, isMobile } = useWindowSize();
  const isGoalEnabled = useTTUStore(getTTUIsGoalEnabled);
  const isSubscribed = useSelector(getIsSubscribed);
  const goalInfo = useSelector((state) => getTopology(state, goalUID));
  const offerList = useSelector((state) => getOfferList(state, goalUID));
  const offerFaqs = useSelector((state) => getOfferFaqs(state));
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [expandedIndexSubs, setExpandedIndexSubs] = useState(0);

  const groupedOffers = useMemo(
    () => groupBy(offerList, 'allowed_duration_days'),
    [offerList]
  );
  const groupedOffersKeys = Object.keys(groupedOffers).sort((a, b) => b - a);

  return (
    <MainWrap>
      <OfferCont>
        <OfferLeftCont>
          <StickyCont>
            <OfferLeftContFlexWrap>
              <OfferIcon $isDarkModeOn={isDarkModeOn}>
                <Image
                  src={offerIcon}
                  alt="offer"
                  width={isTablet ? 84 : 60}
                  height={isTablet ? 84 : 60}
                />
              </OfferIcon>
              <HeaderCont>
                <StyledH2 as="h1">
                  Offers only
                  <br /> for you
                </StyledH2>
                <StyledP color="var(--color-text-secondary)">
                  Get extended subscription at no extra cost
                </StyledP>
              </HeaderCont>
            </OfferLeftContFlexWrap>
            <StyledUnderlineLink
              color="var(--color-i-green)"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                segment.track(CHECKOUT_OFFER_FAQS_VIEWED, {
                  goal_name: goalInfo?.name,
                  goal_uid: goalInfo?.uid
                });
                faqRef.current.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <LinkText color="var(--color-i-green)">Check FAQs</LinkText>
            </StyledUnderlineLink>
          </StickyCont>
        </OfferLeftCont>
        <OfferRightCont>
          <OfferContHead color="var(--color-text-secondary)">
            GET EXTRA MONTHS WITH
          </OfferContHead>
          <StyledDivider />
          {groupedOffersKeys.map((key, index) => {
            const offers = groupedOffers[key];
            const titleTxt = `${pluralize(
              Math.floor(key / 30),
              'month'
            )} subscription`;
            const isExpanded = expandedIndexSubs === index;
            const handleClick = () => {
              if (isExpanded) {
                setExpandedIndexSubs(-1);
              } else {
                setExpandedIndexSubs(index);
              }
            };

            return (
              <>
                <OffersSection
                  isExpanded={isExpanded}
                  handleClick={handleClick}
                  titleTxt={titleTxt}
                  offers={offers}
                />
                <StyledDivider />
              </>
            );
          })}
        </OfferRightCont>
      </OfferCont>
      <FaqWrap ref={faqRef}>
        <Image src={faqIcon} alt="faq" width="48px" height="48px" />
        <FaqHeader>Frequently asked questions</FaqHeader>
        <FaqCont>
          {offerFaqs.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            const handleClick = () => {
              if (isExpanded) {
                setExpandedIndex(-1);
              } else {
                setExpandedIndex(index);
              }
            };

            return (
              <AccordionWrap key={index.toString()}>
                <StyledAccordion expanded={isExpanded}>
                  <StyledAccordionSummary onClick={handleClick}>
                    <FlexCenter>
                      <FaqQuestion color="var(--color-text-primary)">
                        {faq.question}
                      </FaqQuestion>
                      <StyledChevron
                        color="var(--color-text-primary)"
                        size="24px"
                        $expanded={isExpanded}
                      />
                    </FlexCenter>
                  </StyledAccordionSummary>
                  <Accordion.Details>
                    <P1 color="var(--color-text-primary)">{faq.answer}</P1>
                  </Accordion.Details>
                </StyledAccordion>
              </AccordionWrap>
            );
          })}
        </FaqCont>
      </FaqWrap>
      {isGoalEnabled && !isSubscribed && !isMobile && (
        <StyledBanner goalDetails={goalInfo} />
      )}
    </MainWrap>
  );
};

export default Offers;
